import React, { useState, useEffect } from "react";
import { useTemplates, Template } from "../../../hooks/useTemplates";

const TemplatesTab: React.FC = () => {
  // Estados do formulário
  const [templateName, setTemplateName] = useState<string>("");
  const [templateHtml, setTemplateHtml] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null
  );

  // Estados para modais
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null
  );

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Hook de templates
  const {
    templates,
    isLoading,
    error,
    success,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useTemplates();

  // Buscar templates ao montar
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpar mensagens de sucesso após 3 segundos
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        // Success será resetado no próximo fetch
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Funções do formulário
  const handleSubmit = async () => {
    if (!templateName.trim() || !templateHtml.trim()) {
      alert("Por favor, preencha o nome e o código HTML do template.");
      return;
    }

    let result = false;

    if (isEditing && editingTemplateId) {
      result = await updateTemplate(
        editingTemplateId,
        templateName,
        templateHtml
      );
    } else {
      result = await createTemplate(templateName, templateHtml);
    }

    if (result) {
      // Resetar formulário
      setTemplateName("");
      setTemplateHtml("");
      setIsEditing(false);
      setEditingTemplateId(null);
    }
  };

  const handleEdit = (template: Template) => {
    setTemplateName(template.name);
    setTemplateHtml(template.html);
    setIsEditing(true);
    setEditingTemplateId(template.id);
    // Scroll para o formulário
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setTemplateName("");
    setTemplateHtml("");
    setIsEditing(false);
    setEditingTemplateId(null);
  };

  const handlePreview = (html: string) => {
    setPreviewHtml(html);
    setShowPreviewModal(true);
  };

  const handleDeleteClick = (template: Template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    const result = await deleteTemplate(templateToDelete.id);
    if (result) {
      // Ajustar página atual se necessário
      const newTotalPages = Math.ceil((templates.length - 1) / pageSize);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    }

    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  // Paginação
  const totalPages = Math.ceil(templates.length / pageSize);
  const getPaginatedTemplates = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return templates.slice(startIndex, endIndex);
  };

  // Loading state
  if (isLoading && templates.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <span>Carregando templates...</span>
      </div>
    );
  }

  return (
    <div className="templates-tab">
      <h2>{isEditing ? "Editar Template" : "Criar Novo Template"}</h2>
      <p>
        {isEditing
          ? "Atualize as informações do template"
          : "Crie templates HTML para seus materiais"}
      </p>

      {/* Formulário */}
      <div className="template-form">
        <div className="form-group">
          <label htmlFor="templateName">Nome do Template:</label>
          <input
            id="templateName"
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Ex: Template Material Matemática"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="templateHtml">Código HTML:</label>
          <textarea
            id="templateHtml"
            value={templateHtml}
            onChange={(e) => setTemplateHtml(e.target.value)}
            placeholder="Cole seu código HTML aqui..."
            className="form-textarea"
            rows={15}
            style={{ fontFamily: "monospace", fontSize: "14px" }}
          />
          <small className="form-help">
            💡 Cole o código HTML completo do seu template
          </small>
        </div>

        <div
          className="form-actions"
          style={{ display: "flex", gap: "12px", marginTop: "20px" }}
        >
          <button
            className="preview-button"
            onClick={() => handlePreview(templateHtml)}
            disabled={!templateHtml.trim()}
            type="button"
            style={{
              padding: "12px 24px",
              backgroundColor: !templateHtml.trim() ? "#666" : "#5B7FA6",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: !templateHtml.trim() ? "not-allowed" : "pointer",
              fontSize: "16px",
              fontWeight: 500,
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              if (templateHtml.trim()) {
                e.currentTarget.style.backgroundColor = "#4A6A8A";
              }
            }}
            onMouseLeave={(e) => {
              if (templateHtml.trim()) {
                e.currentTarget.style.backgroundColor = "#5B7FA6";
              }
            }}
          >
            👁️ Preview
          </button>

          {isEditing && (
            <button
              className="cancel-button"
              onClick={handleCancelEdit}
              type="button"
              style={{
                padding: "12px 24px",
                backgroundColor: "#f44336",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: 500,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#d32f2f";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f44336";
              }}
            >
              ❌ Cancelar
            </button>
          )}

          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={!templateName.trim() || !templateHtml.trim() || isLoading}
            type="button"
            style={{
              padding: "12px 24px",
              backgroundColor:
                !templateName.trim() || !templateHtml.trim() || isLoading
                  ? "#666"
                  : "#5B7FA6",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor:
                !templateName.trim() || !templateHtml.trim() || isLoading
                  ? "not-allowed"
                  : "pointer",
              fontSize: "16px",
              fontWeight: 500,
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              if (templateName.trim() && templateHtml.trim() && !isLoading) {
                e.currentTarget.style.backgroundColor = "#4A6A8A";
              }
            }}
            onMouseLeave={(e) => {
              if (templateName.trim() && templateHtml.trim() && !isLoading) {
                e.currentTarget.style.backgroundColor = "#5B7FA6";
              }
            }}
          >
            {isLoading
              ? "Salvando..."
              : isEditing
              ? "✅ Atualizar Template"
              : "✅ Criar Template"}
          </button>
        </div>

        {error && <div className="upload-error">❌ {error}</div>}
        {success && (
          <div className="upload-success">
            ✅ Template {isEditing ? "atualizado" : "criado"} com sucesso!
          </div>
        )}
      </div>

      {/* Lista de Templates */}
      <div className="templates-list-section" style={{ marginTop: "60px" }}>
        <h3>📋 Meus Templates</h3>

        {templates.length === 0 ? (
          <p>Nenhum template encontrado. Crie seu primeiro template!</p>
        ) : (
          <>
            <div className="templates-table-container">
              <table className="templates-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Data Criação</th>
                    <th>Última Atualização</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedTemplates().map((template) => (
                    <tr key={template.id} className="template-row">
                      <td className="template-name-cell">
                        <span className="template-name">{template.name}</span>
                      </td>
                      <td className="template-date-cell">
                        {new Date(template.createdAt).toLocaleDateString(
                          "pt-BR"
                        )}
                      </td>
                      <td className="template-date-cell">
                        {new Date(template.updatedAt).toLocaleDateString(
                          "pt-BR"
                        )}
                      </td>
                      <td className="template-actions-cell">
                        <div className="action-buttons">
                          <button
                            className="preview-content-btn"
                            onClick={() => handlePreview(template.html)}
                            title="Visualizar preview"
                          >
                            👁️
                          </button>
                          <button
                            className="edit-template-btn"
                            onClick={() => handleEdit(template)}
                            title="Editar template"
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-content-btn"
                            onClick={() => handleDeleteClick(template)}
                            disabled={isLoading}
                            title="Deletar template"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Página {currentPage} de {totalPages} ({templates.length}{" "}
                  templates)
                </div>

                <div className="pagination-buttons">
                  {currentPage > 1 && (
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      ← Anterior
                    </button>
                  )}

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      return (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      );
                    })
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="pagination-dots">...</span>
                        )}

                        <button
                          className={`pagination-btn ${
                            currentPage === page ? "active" : ""
                          }`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}

                  {currentPage < totalPages && (
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Próxima →
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Preview */}
      {showPreviewModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowPreviewModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#2B2B2B",
              padding: "30px",
              borderRadius: "12px",
              maxWidth: "90%",
              maxHeight: "90%",
              width: "900px",
              overflow: "auto",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <h2
                style={{
                  color: "#fff",
                  fontSize: "24px",
                  marginBottom: "10px",
                  fontWeight: 500,
                }}
              >
                👁️ Preview do Template
              </h2>
              <p style={{ color: "#D9D9D9", fontSize: "14px" }}>
                Visualização do HTML renderizado
              </p>
            </div>

            <div
              style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                minHeight: "400px",
                overflow: "auto",
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>

            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowPreviewModal(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Delete */}
      {showDeleteModal && templateToDelete && (
        <div
          className="modal-overlay"
          onClick={handleCancelDelete}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#2B2B2B",
              padding: "30px",
              borderRadius: "12px",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <h2
                style={{
                  color: "#fff",
                  fontSize: "24px",
                  marginBottom: "10px",
                  fontWeight: 500,
                }}
              >
                🗑️ Confirmar Exclusão
              </h2>
              <p
                style={{ color: "#D9D9D9", fontSize: "16px", lineHeight: 1.6 }}
              >
                Tem certeza que deseja deletar o template{" "}
                <strong style={{ color: "#fff" }}>
                  "{templateToDelete.name}"
                </strong>
                ?
              </p>
              <p
                style={{
                  color: "#FF9800",
                  fontSize: "14px",
                  marginTop: "10px",
                }}
              >
                ⚠️ Esta ação não pode ser desfeita.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCancelDelete}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isLoading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: isLoading ? "#666" : "#f44336",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                {isLoading ? "Deletando..." : "Deletar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesTab;
